
export interface FontVariant {
  id: number;
  family_name: string;
  weight: number;
  italic: boolean;
  format: string;
  source: string;
  variable: boolean;
  axes_json: any[] | null;
  file_url?: string;
}

export interface BrandColor {
  name: string;
  value: number[];
}

export interface BrandImage {
  name: string;
  url: string;
}

export interface BrandAssets {
  colors: BrandColor[];
  images: BrandImage[];
  fonts: FontVariant[];
}

/** What a brand asset is for (MPN-1712). Each kind is its own section of the Branding tab. */
export type BrandAssetKind = "LOGO" | "ICON" | "FAVICON" | "GRAPHIC_PATTERN" | "LIBRARY";

/** Which variant a logo is. A brand holds at most one live Primary and one live Square Format. */
export type LogoType =
  | "PRIMARY"
  | "LIGHT_BACKGROUND"
  | "DARK_BACKGROUND"
  | "ICON_ONLY"
  | "HORIZONTAL"
  | "STACKED_VERTICAL"
  | "SQUARE_FORMAT";

export interface BrandAsset {
  id: number;
  tag: string | null;
  kind: BrandAssetKind;
  /** Only on a logo. */
  logo_type: LogoType | null;
  /** The asset's own name, or its file's when it has none. */
  name: string | null;
  url: string | null;
  description: string | null;
  /** Free text, e.g. "120px wide, 10px clear space". */
  minimum_size: string | null;
  usage_guidelines: string | null;
  /** Read off the file: "image" or "video" ("other" for an odd legacy file). */
  media_type: string;
  width: number | null;
  height: number | null;
}

export type Capitalization = "TITLE_CASE" | "SENTENCE_CASE";

export class Brand {
  id: string;
  name: string;
  company_site_url : string;
  primary_color_code : string;
  secondary_color_code: string;
  tertiary_color_code: string;
  logo_url : string;
  logo: File | null;
  tag_line: string;
  fonts: FontVariant[];
  assets: BrandAsset[];
  // Brand info and editorial guidelines (MPN-1712), saved through the PATCH.
  // Text is "" when unset; the Yes/No answers are null until somebody answers.
  display_name: string;
  guidelines_url: string;
  demographics: string;
  capitalization: Capitalization | "";
  oxford_comma: boolean | null;
  em_dash_spaces: boolean | null;
  ampersand_for_and: boolean | null;
  key_terms: string;
  brand_notes: string;
  /** A code from the server's brand date formats (MPN-2071); null means not set, so the default applies. */
  preferred_date_format: string | null;
  /** The brand's live typography styles (MPN-2033). */
  typography_styles: BrandTypographyStyle[];

  constructor() {
    this.id = "";
    this.name = "";
    this.company_site_url  = "";
    this.logo_url = "";
    this.logo = null;
    this.primary_color_code = "";
    this.secondary_color_code = "";
    this.tertiary_color_code = "";
    this.tag_line = "";
    this.fonts = [];
    this.assets = [];
    this.display_name = "";
    this.guidelines_url = "";
    this.demographics = "";
    this.capitalization = "";
    this.oxford_comma = null;
    this.em_dash_spaces = null;
    this.ampersand_for_and = null;
    this.key_terms = "";
    this.brand_notes = "";
    this.preferred_date_format = null;
    this.typography_styles = [];
  }
}



/** A brand color's category. PRIMARY and SECONDARY belong to the legacy color slots for now. */
export type BrandColorCategory = "PRIMARY" | "SECONDARY" | "NEUTRAL" | "ACCENT" | "ACTION" | "TEXT" | "OTHER";

/**
 * One entry in a brand's color list (MPN-1712). The three legacy color slots on Brand come
 * first, as `legacy:primary`, `legacy:secondary` and `legacy:tertiary` (shown as Accent),
 * and hold only a hex. Every other color is a brand color row, addressed by its uuid.
 */
export interface BrandColorItem {
  id: string;
  source: "legacy" | "table";
  category: BrandColorCategory;
  hex_code: string;
  name: string | null;
  usage_guidelines: string | null;
  application_guidelines: string | null;
  /** A legacy slot still at its column default, i.e. nobody has set it. */
  is_default: boolean;
  /** False for legacy values that were never #RRGGBB; they are shown as stored. */
  is_valid_hex: boolean;
}

/** A brand-scoped `token_value` ad token, as the brand page offers it. `token` keeps its brackets: `[ADVISOR]`. */
export interface BrandAdToken {
  uuid: string;
  token: string;
  help_text: string;
}

/**
 * One of the server's brand date formats (MPN-2071), in display order. `pattern` is in Luxon
 * tokens ("MMMM d, yyyy"); `is_default` marks the one used when a brand hasn't picked one.
 */
export interface BrandDateFormat {
  code: string;
  label: string;
  pattern: string;
  is_default: boolean;
}

/** One live ad token value on a brand (MPN-1712, D2): `[ADVISOR]` → "Adviser". */
export interface BrandAdTokenValue {
  uuid: string;
  ad_token_definition: BrandAdToken;
  value: string;
}

export type TypographyCategory = "heading" | "display" | "body" | "caption" | "quote" | "button";

/** Where a style's family comes from: the Google Fonts catalog, or a font uploaded and linked to the brand. */
export type TypographySource = "google" | "uploaded";

/**
 * A brand typography style (MPN-2033): a named Font Family for one Category of text. It holds the
 * family by name only, no weight or italic; Create's Bold/Italic controls set those. An `uploaded`
 * style names a family already linked to the brand as a font.
 */
export interface BrandTypographyStyle {
  id: number;
  name: string;
  category: TypographyCategory;
  family_name: string;
  source: TypographySource;
  usage_guidelines: string;
}
